import { Component, ElementRef, ViewChild } from '@angular/core';
import {
  Conditions,
  UserPermissionsModel,
} from '../../../../Models/ApplicationModels';
import { MasterDataSeriveService } from '../../../../Services/master-data-serive.service';
import { ExportExcelService } from '../../../../Services/export-excel.service';
import { CustomToastsService } from '../../../../Services/custom-toasts.service';
import { NgxPermissionsService } from 'ngx-permissions';
import { UserserviceService } from 'src/app/Services/userservice.service';
import { ImportServiceService } from 'src/app/Services/import-service.service';
import * as XLSX from 'xlsx';
import FileSaver from 'file-saver';

@Component({
  selector: 'app-conditions',
  templateUrl: './conditions.component.html',
  styleUrls: ['./conditions.component.css'],
})
export class ConditionsComponent {
  // Core data
  conditions: Array<Conditions> = [];
  condition: Conditions = new Conditions();
  exportList: any = [];
  
  // UI state
  headerCheckboxSelected: boolean = false;
  selectedConditionIDs: string[] = [];
  selectedRowIndex: number | null = null;
  frmCondnText: string = 'Add New Condition';
  hideUpdateBtn: boolean = true;
  isUpdated = false;
  
  // Delete modal state
  deleteParameter: any = null;
  deleteButtonClickedWithoutSelection: boolean = false;
  deleteButtonClickedWithSelection: boolean = false;
  
  // Import/Export state
  exportColumnsArr = [['Condition', 'Condition Code', 'Condition Color']];
  importConditionsList: any[] = [];
  isImportDisabled: boolean = true;
  toggleAll: boolean = false;
  isInvalidImport: boolean = false;

  @ViewChild('modalConfirmCloseButton')
  modalConfirmCloseButton?: ElementRef<HTMLElement>;
  @ViewChild('importCondition') importCondition!: ElementRef<HTMLButtonElement>;
  @ViewChild('importModel1') importModel1!: ElementRef<HTMLButtonElement>;

  constructor(
    private permissionService: NgxPermissionsService,
    private userservice: UserserviceService,
    private masterservice: MasterDataSeriveService,
    private toastr: CustomToastsService,
    private exportExcelService: ExportExcelService,
    private importService: ImportServiceService
  ) {}

  ngOnInit() {
    this.getAllConditions();
    this.getPermissionsByUserId();
  }

  // Toast notification helpers
  private showSuccess(): void {
    this.toastr.success('New Condition has been successfully added.');
  }

  private showUpdated(): void {
    this.toastr.success('Condition has been successfully updated.');
  }

  private showError(): void {
    this.toastr.error('Condition Already Exist');
  }

  isSelected(condition: Conditions): boolean {
    return this.selectedConditionIDs.includes(condition.conditionId.toString());
  }

  highlightedRow(id: any): void {
    this.selectedRowIndex = id ? Number(id) : null;
  }

  // Data fetching and preparation
  getAllConditions(): void {
    const userdata = this.masterservice.getUserDetailsFromStorage();
    const companyId = userdata.companyID;
    
    this.masterservice.getAllConditions(companyId).subscribe((res) => {
      if (res) {
        this.conditions = res as Conditions[];
        this.prepareExportList();
      }
    });
  }

  private prepareExportList(): void {
    this.exportList = this.conditions.map((cnd) => ({
      Condition: cnd.conditionDescr,
      Condition_Code: cnd.conditionCode,
      Condition_Color: cnd.conditionColor,
      Date_Created: this.formatDateForExport(cnd.createdDt),
      Created_By: cnd.userName,
    }));
  }

  private formatDateForExport(dateString: string): string {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${month}-${day}-${year}`;
  }

  exportToExcel(): void {
    this.exportExcelService.exportAsExcelFile(
      this.exportList,
      'Conditions-data'
    );
  }

  // Form management
  clearForm(): void {
    this.hideUpdateBtn = true;
    this.frmCondnText = 'Add New Condition';
    this.condition = new Conditions();
    this.isUpdated = false;
  }

  createConditon(condition: Conditions): void {
    const userdata = this.masterservice.getUserDetailsFromStorage();
    condition.companyId = userdata.companyID;
    condition.createdByUserId = userdata.userId;
    
    this.masterservice.addUpdateConditions(condition).subscribe(
      (res) => {
        if (res) {
          this.getAllConditions();
          this.isUpdated ? this.showUpdated() : this.showSuccess();
          this.isUpdated = false;
        }
      },
      (err) => {
        this.showError();
      }
    );
  }

  getConditionById(id: number): void {
    this.hideUpdateBtn = false;
    this.frmCondnText = 'Update Condition';
    this.isUpdated = true;
    
    // Find the condition from existing array instead of making API call
    const selectedCondition = this.conditions.find(condition => condition.conditionId === id);
    if (selectedCondition) {
      this.condition = { ...selectedCondition }; // Create a copy to avoid direct mutation
    }
  }

  private uncheckSelectedCheckboxes(): void {
    this.selectedConditionIDs = [];
    this.headerCheckboxSelected = false;
    this.conditions.forEach(condition => condition.isSelected = false);
  }

  // Delete operations
  openConfirmPopup(conditionID: any): void {
    const hasSelection = conditionID !== null && !(Array.isArray(conditionID) && conditionID.length === 0);
    
    this.deleteButtonClickedWithSelection = hasSelection;
    this.deleteButtonClickedWithoutSelection = !hasSelection;
    this.deleteParameter = hasSelection ? conditionID : null;
  }

  // Checkbox selection management
  toggleAllCheckboxes(): void {
    this.headerCheckboxSelected = !this.headerCheckboxSelected;
    
    if (this.headerCheckboxSelected) {
      this.selectedConditionIDs = this.conditions.map(condition => condition.conditionId.toString());
      this.conditions.forEach(condition => condition.isSelected = true);
    } else {
      this.uncheckSelectedCheckboxes();
    }
  }

  handleCheckboxChangeC(event: any, condition: Conditions): void {
    const conditionIdStr = condition.conditionId.toString();
    
    if (event.target.checked) {
      this.selectedConditionIDs.push(conditionIdStr);
      condition.isSelected = true;
    } else {
      const index = this.selectedConditionIDs.indexOf(conditionIdStr);
      if (index !== -1) {
        this.selectedConditionIDs.splice(index, 1);
      }
      condition.isSelected = false;
      this.headerCheckboxSelected = false;
    }
    
    // Update header checkbox if all items are selected
    this.headerCheckboxSelected = this.selectedConditionIDs.length === this.conditions.length;
  }

  private closeModal(): void {
    this.modalConfirmCloseButton?.nativeElement.click();
  }

  deleteConditon(deleteParameter: number | number[]): void {
    const idsArray = Array.isArray(deleteParameter) ? deleteParameter : [deleteParameter];
    const selectedIds = idsArray.join(',');
    
    this.masterservice.removeConditions(selectedIds).subscribe((res) => {
      if (res) {
        this.toastr.success('Condition has been successfully deleted.');
        this.getAllConditions();
        this.uncheckSelectedCheckboxes();
        this.closeModal();
      }
    });
  }

  // Permissions
  private getPermissionsByUserId(): void {
    this.userservice.getUserRolesandPerm().then((res) => {
      const permissions = res as UserPermissionsModel[];
      const permissionNames = permissions?.map(pr => pr.name) || [];
      this.permissionService.loadPermissions(permissionNames);
    });
  }

  // Import/Export operations
  exportConditionTemplate(): void {
    const ws: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet(this.exportColumnsArr);
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Condition');
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    FileSaver.saveAs(new Blob([wbout]), 'Condition.xlsx');
  }
  importConditionColor(): void {
    this.importCondition?.nativeElement.click();
  }

  onFileChangeImport(event: any): void {
    const user = this.masterservice.getUserDetailsFromStorage();
    const file = event.target.files[0];

    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e: any) => {
      const workbook = XLSX.read(e.target.result, { type: 'binary' });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const rawData: any[][] = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][];
      
      const filteredData = rawData.filter(row => 
        row.some(cell => cell !== null && cell !== undefined && cell !== '')
      );

      this.importConditionsList = this.transformToJsonArray(filteredData);
      this.initializeImportList(user);
      this.importModel1?.nativeElement.click();
    };

    reader.readAsBinaryString(file);
  }

  private initializeImportList(user: any): void {
    const outletID = JSON.parse(localStorage.getItem('outletID') || 'null');
    
    this.importConditionsList.forEach(item => {
      item.createdByUserId = user.userId;
      item.companyId = user.companyID;
      item.outletID = outletID;
      item.isChecked = false;
      item.addStatus = 2;
      item.status = null;
      item.conditionCode = item.conditionCode ? item.conditionCode.toString().trim() : '';
    });
  }
  private transformToJsonArray(data: any[][]): any[] {
    if (data.length === 0) return [];

    const headerMapping: { [key: string]: string } = {
      'Condition': 'conditionDescr',
      'Condition Code': 'conditionCode',
      'Condition Color': 'conditionColor',
    };

    const headers = data[0];
    const camelCaseHeaders = headers.map(header => 
      headerMapping[header] || header.toLowerCase().replace(/\s+/g, '')
    );

    return data.slice(1).map(row => {
      const obj: any = {};
      camelCaseHeaders.forEach((header, index) => {
        obj[header] = row[index];
      });
      return obj;
    });
  }
  isImportEnabled(): void {
    const hasCheckedItems = this.importConditionsList.some(item => item.isChecked);
    this.isImportDisabled = !hasCheckedItems;
  }
  closeImportModal(): void {
    // Reset file input to allow re-selection of the same file
    if (this.importCondition) {
      this.importCondition.nativeElement.value = '';
    }
  }

  checkAllImport(event: Event): void {
    const isChecked = (event.target as HTMLInputElement).checked;
    this.toggleAll = isChecked;
    
    this.importConditionsList.forEach(item => {
      if (item.addStatus === 2) {
        item.isChecked = isChecked;
      }
    });
    
    this.isImportEnabled();
  }
  addImportedcondition(): void {
    const userinfo = this.masterservice.getUserDetailsFromStorage();
    const conditionList = this.importConditionsList
      .filter(item => item.isChecked)
      .map(({ status, ...rest }) => rest);

    if (conditionList.length === 0) {
      this.toastr.error('Invalid Selection');
      return;
    }

    // Validate entries
    const hasInvalidEntry = conditionList.some(condition => 
      !condition.conditionDescr && !condition.conditionCode
    );

    if (hasInvalidEntry) {
      this.toastr.error('Invalid entry in import file');
      return;
    }

    // Prepare condition list for import
    conditionList.forEach(condition => {
      condition.active = true;
      condition.companyId = userinfo.companyID;
    });

    this.importService.ImportConditions(conditionList).subscribe((res) => {
      this.importConditionsList = res as any[];
      this.processImportResponse();
      this.isImportDisabled = true;
      this.toggleAll = false;
      this.getAllConditions();
    });
  }

  private processImportResponse(): void {
    this.importConditionsList.forEach(item => {
      if (item.reason != null) {
        item.reason = item.reason
          .split(',')
          .map((reason: string) => reason.trim())
          .filter((reason: string) => reason.length > 0);
      }
    });
  }

  getRemainingErrors(reasonString: string): string {
    const errors = reasonString.split('|');
    return errors.slice(1).join('\n');
  }

}
